var html2canvas = require("html2canvas");
var jsPDF = require("jspdf").jsPDF;

/* A4 in millimetres, the unit the whole module works in */
const A4 = { width: 210, height: 297 };
const DEFAULTS = {
  background: "#0a0e17",
  orientation: "landscape",
  margin: 8,
  scale: 2,
  /* the capture is a full colour screenshot: PNG keeps it lossless but lands around 14 MB,
     JPEG at this quality is visually identical on screen and roughly twenty times smaller */
  imageFormat: "JPEG",
  imageQuality: 0.92
};

function pageSize(orientation) {
  if (orientation === "landscape") return { width: A4.height, height: A4.width };
  return { width: A4.width, height: A4.height };
}

function hexToRgb(hex) {
  const clean = String(hex).replace("#", "");
  const full = clean.length === 3
    ? clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2]
    : clean;
  return {
    r: parseInt(full.substring(0, 2), 16) || 0,
    g: parseInt(full.substring(2, 4), 16) || 0,
    b: parseInt(full.substring(4, 6), 16) || 0
  };
}

/* Chart.js draws on canvases and html2canvas does not always copy them faithfully. Painting
   the original straight into the cloned canvas is synchronous: turning them into <img> tags
   instead leaves the capture waiting on an image decode, which Safari loses the race to and
   renders the charts as empty boxes. */
function copyCanvases(element, cloned) {
  const originals = element.querySelectorAll("canvas");
  const clones = cloned.querySelectorAll("canvas");

  for (let i = 0; i < clones.length; i++) {
    const source = originals[i];
    if (!source || !source.width || !source.height) continue;

    const target = clones[i];
    try {
      /* assigning the size also clears the clone, so it is drawn fresh either way */
      target.width = source.width;
      target.height = source.height;
      target.getContext("2d").drawImage(source, 0, 0);
    } catch (error) {
      /* a tainted canvas cannot be read, html2canvas keeps whatever it cloned itself */
    }
  }
}

/* Releases the inner clipping so the content can grow downwards into the extra height asked
   for in `captureHeight`. The captured node keeps its on screen width pinned: it is a flex
   child, and letting it size to its content instead would reflow the whole capture. */
function unlockLayout(cloned, width) {
  cloned.style.overflow = "visible";
  cloned.style.height = "auto";
  cloned.style.width = width + "px";
  cloned.style.flex = "0 0 auto";

  Array.prototype.forEach.call(cloned.querySelectorAll(".buses-detail-area"), function (node) {
    node.style.overflow = "visible";
    node.style.height = "auto";
    node.style.flex = "none";
  });

  /* html2canvas ignores CSS filters and 3D transforms, leaving them skews the donut */
  const donut = cloned.querySelector(".buses-donut-box");
  if (donut) {
    donut.style.filter = "none";
    donut.style.transform = "none";
  }

  Array.prototype.forEach.call(cloned.querySelectorAll("[data-export-hide]"), function (node) {
    node.style.display = "none";
  });
}

/* Whatever an inner scroller is hiding still has to end up in the file */
function captureHeight(element) {
  var hidden = 0;
  Array.prototype.forEach.call(element.querySelectorAll(".buses-detail-area"), function (node) {
    hidden += Math.max(0, node.scrollHeight - node.clientHeight);
  });
  return element.scrollHeight + hidden;
}

/* Places the capture on as many pages as it needs, always over the background colour so
   the dark theme reaches the margins instead of sitting on white paper */
function addImagePages(pdf, canvas, options) {
  const page = pageSize(options.orientation);
  const margin = options.margin;
  const usableWidth = page.width - margin * 2;
  const usableHeight = page.height - margin * 2;
  const background = hexToRgb(options.background);
  const mime = options.imageFormat === "PNG" ? "image/png" : "image/jpeg";
  /* millimetres of the capture that fit on one page, converted back to source pixels */
  const pxPerMm = canvas.width / usableWidth;
  const sliceHeightPx = Math.max(1, Math.floor(usableHeight * pxPerMm));

  var offset = 0;
  var first = true;

  while (offset < canvas.height) {
    if (!first) pdf.addPage();
    first = false;

    pdf.setFillColor(background.r, background.g, background.b);
    pdf.rect(0, 0, page.width, page.height, "F");

    const height = Math.min(sliceHeightPx, canvas.height - offset);
    var source = canvas;

    if (height < canvas.height) {
      source = document.createElement("canvas");
      source.width = canvas.width;
      source.height = height;
      const context = source.getContext("2d");
      context.fillStyle = options.background;
      context.fillRect(0, 0, source.width, source.height);
      context.drawImage(canvas, 0, offset, canvas.width, height, 0, 0, canvas.width, height);
    }

    pdf.addImage(
      source.toDataURL(mime, options.imageQuality),
      options.imageFormat,
      margin,
      margin,
      usableWidth,
      height / pxPerMm
    );

    offset += height;
  }
}

/**
 * Renders a DOM element into a downloaded PDF file.
 *
 * @param {HTMLElement} element node to capture
 * @param {Object} [options] filename, background, orientation, margin, scale and an
 *                           onClone(clonedElement, clonedDocument) hook to touch up the copy
 * @returns {Promise} resolves with the filename once handed to the browser
 */
function exportElementToPdf(element, options) {
  const settings = Object.assign({}, DEFAULTS, options || {});
  const filename = settings.filename || "export.pdf";

  if (!element) return Promise.reject(new Error("No element to export"));

  const width = element.getBoundingClientRect().width;

  return html2canvas(element, {
    backgroundColor: settings.background,
    scale: settings.scale,
    useCORS: true,
    logging: false,
    width: width,
    height: captureHeight(element),
    onclone: function (doc, cloned) {
      copyCanvases(element, cloned);
      unlockLayout(cloned, width);
      if (settings.onClone) settings.onClone(cloned, doc);
    }
  }).then(function (canvas) {
    const pdf = new jsPDF({
      orientation: settings.orientation,
      unit: "mm",
      format: "a4"
    });
    addImagePages(pdf, canvas, settings);
    pdf.save(filename);
    return filename;
  });
}

module.exports = {
  exportElementToPdf: exportElementToPdf
};
